import { Cover } from '@app/shared/models/cover';
import { Logo } from '@app/shared/models/logo';
import { Promotion } from '@app/shared/models/promotion';
import { TransferFile } from '@app/shared/models/transferFile';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { Constant } from 'src/constant';
import { ViewerActions } from '../actions';
import moment from 'moment';
import { errors, GetTransferPreviewOutput } from '@smash-sdk/transfer/ejs/01-2024';
import { SDKError } from '@smash-sdk/core';

export const viewerFeatureKey = 'viewer';

export interface FilesState extends EntityState<TransferFile> { }
const filesAdapter = createEntityAdapter<TransferFile>({
  selectId: (file) => file.id,
});
const filesInitialState: FilesState = filesAdapter.getInitialState({});
export interface State {
  files: FilesState;
  filesLoading: boolean;
  filesLoaded: boolean;
  filesError: any;
  filesUrlsExpiry: string;
  nextFile: string;
  urlsToDownload: string[];
  completeTransferDownloadUrl: string;
  downloadDisplayed: boolean;

  transferMetas: any;
  transferMetasError: SDKError['code'];

  preview: GetTransferPreviewOutput;
  previewLoaded: boolean;
  previewLoading: boolean;
  previewLoadingError: any;

  promotion: Promotion;
  promotionLoaded: boolean;
  promotionLoading: boolean;
  promotionError: any;

  cover: Cover;
  coverLoaded: boolean;
  coverLoading: boolean;
  coverError: any;

  logo: Logo;
  logoLoaded: boolean;
  logoLoading: boolean;
  logoError: any;

  snackbarDisplayed: boolean;
  modalPreviewDisplayed: boolean;
  coverBackgroundLoaded: boolean;
  logoImageLoaded: boolean;
  selectedModalFile: TransferFile;
  selectedFileOffsetY?: number;
  selectedFiles: TransferFile[];
  errorMessage: any;
  menuDisplayed: boolean;
  passwordRequired: boolean;
  previewUnlockError: string;
  previewPassword: string;

  accessTrackingRequired: boolean;
  previewAccessTracking: string;
}

export const initialState: State = {
  files: filesInitialState,
  filesLoading: false,
  filesLoaded: false,
  filesError: null,
  filesUrlsExpiry: null,
  nextFile: null,
  urlsToDownload: null,
  completeTransferDownloadUrl: null,
  downloadDisplayed: false,

  transferMetas: null,
  transferMetasError: null,

  preview: null,
  previewLoaded: false,
  previewLoading: false,
  previewLoadingError: false,

  promotion: null,
  promotionLoaded: false,
  promotionLoading: false,
  promotionError: null,

  cover: null,
  coverLoaded: false,
  coverLoading: false,
  coverError: null,

  logo: null,
  logoLoaded: false,
  logoLoading: false,
  logoError: null,

  coverBackgroundLoaded: false,
  logoImageLoaded: false,

  previewUnlockError: null,

  snackbarDisplayed: false,
  modalPreviewDisplayed: false,
  selectedModalFile: null,
  selectedFiles: [],
  errorMessage: null,
  menuDisplayed: false,
  passwordRequired: false,
  previewPassword: '',

  accessTrackingRequired: false,
  previewAccessTracking: '',
};


export const reducer = createReducer(
  initialState,
  on(ViewerActions.LoadTransferMetas, (state) => ({
    ...state,
    transferMetas: null,
    transferMetasError: null
  })),
  on(ViewerActions.LoadTransferMetasSuccess, (state, { transfer }) => ({
    ...state,
    transferMetas: transfer,
  })),
  on(ViewerActions.LoadTransferMetasFailure, (state, { error }) => ({
    ...state,
    transferMetasError: error,
  })),
  on(ViewerActions.LoadPreview, (state, { password = '', accessTracking = '' }) => ({
    ...state,
    preview: initialState.preview,
    previewLoaded: initialState.previewLoaded,
    previewLoading: true,
    previewUnlockError: initialState.previewUnlockError,
    previewLoadingError: initialState.previewLoadingError,
    previewPassword: password,
    previewAccessTracking: accessTracking,
  })),
  on(ViewerActions.LoadPreviewSuccess, (state, { preview }) => ({
    ...state,
    preview,
    previewLoaded: true,
    previewLoading: initialState.previewLoading,
    passwordRequired: false,
    accessTrackingRequired: false,
    loading: false,
  })),
  on(ViewerActions.LoadPreviewFailure, (state, { error }) => ({
    ...state,
    previewLoadingError: error,
    password: '',
    loading: false,
  })),

  // Lock unlock preview
  on(ViewerActions.LockPreview, (state, { passwordRequired, accessTrackingRequired }) => ({
    ...state,
    passwordRequired,
    accessTrackingRequired,
    errorMessage: initialState.errorMessage,
    previewLoading: initialState.previewLoading,
  })),
  on(ViewerActions.UnlockPreview, (state) => ({
    ...state,
    passwordRequired: initialState.passwordRequired,
    previewLoading: true,
    previewUnlockError: initialState.previewUnlockError,
  })),
  on(ViewerActions.UnlockPreviewSuccess, (state) => ({
    ...state,
    passwordRequired: initialState.passwordRequired,
    previewLoading: initialState.previewLoading,
  })),
  on(ViewerActions.UnlockPreviewFailure, (state, { errorType }) => ({
    ...state,
    previewUnlockError: errorType,
    previewLoading: false,
  })),

  // Load Cover
  on(ViewerActions.LoadCover, (state) => ({
    ...state,
    cover: initialState.cover,
    coverLoading: true,
    coverLoaded: initialState.coverLoaded,
    coverError: initialState.coverError,
  })),
  on(ViewerActions.LoadCoverSuccess, (state, { cover }) => ({
    ...state,
    cover,
    coverLoading: initialState.coverLoading,
    coverLoaded: true,
  })),
  on(ViewerActions.LoadCoverFailure, (state, { error }) => ({
    ...state,
    coverLoading: initialState.coverLoading,
    coverError: error,
  })),

  // Load Promotion
  on(ViewerActions.LoadPromotion, (state) => ({
    ...state,
    promotion: initialState.promotion,
    promotionLoading: true,
    promotionLoaded: initialState.promotionLoaded,
    promotionError: initialState.promotionError,
  })),
  on(ViewerActions.LoadPromotionSuccess, (state, { promotion }) => ({
    ...state,
    promotion,
    promotionLoading: initialState.promotionLoading,
    promotionLoaded: true,
  })),
  on(ViewerActions.LoadPromotionFailure, (state, { error }) => ({
    ...state,
    error,
    promotionLoading: initialState.promotionLoading,
    promotionError: error,
  })),

  // Modal preview
  on(ViewerActions.DisplayModalPreview, (state) => ({
    ...state,
    modalPreviewDisplayed: true,
  })),
  on(ViewerActions.CloseModalPreview, (state) => ({
    ...state,
    modalPreviewDisplayed: false,
  })),

  on(ViewerActions.SelectFileToPreview,
    (state, { file, offsetY }) => ({
      ...state,
      selectedModalFile: file,
      selectedFileOffsetY: offsetY,
    })),
  on(ViewerActions.AddFileToDownloadList, (state, { file }) => ({
    ...state,
    selectedFiles: [...state.selectedFiles, file],
  })),
  on(ViewerActions.RemoveFileFromDownloadList, (state, { file }) => ({
    ...state,
    selectedFiles: state.selectedFiles.filter(_file => { if (_file.name === file.name) { return false; } return true; }),
  })),
  on(ViewerActions.ToggleMenu, (state, { menuDisplayed }) => ({
    ...state,
    menuDisplayed,
  })),
  on(ViewerActions.ResetSelectedFiles, (state) => ({
    ...state,
    selectedFiles: initialState.selectedFiles,
  })),

  // Files 
  on(ViewerActions.LoadFiles, (state) => ({
    ...state,
    filesLoading: true,
    files: initialState.files,
    filesUrlsExpiry: initialState.filesUrlsExpiry,
    filesLoaded: initialState.filesLoaded,
    filesError: initialState.filesError,
  })),
  on(ViewerActions.LoadFilesSuccess, (state, { files, next }) => ({
    ...state,
    files: filesAdapter.upsertMany(files, state.files),
    nextFile: next || null,
    filesLoading: initialState.filesLoading,
    filesLoaded: true,
  })),
  on(ViewerActions.LoadFilesFailure, (state, { error }) => ({
    ...state,
    filesError: error,
    filesLoading: initialState.filesLoading,
  })),
  // Files - load more files
  on(ViewerActions.LoadMoreFiles, (state) => ({
    ...state,
    filesLoading: true,
  })),
  on(ViewerActions.LoadMoreFilesSuccess, (state, { files, next }) => ({
    ...state,
    files: filesAdapter.upsertMany(files, state.files),
    nextFile: next || null,
    filesLoading: initialState.filesLoading,
    filesLoaded: true,
  })),
  on(ViewerActions.LoadMoreFilesFailure, (state, { error }) => ({
    ...state,
    filesError: error,
    filesLoading: initialState.filesLoading,
  })),
  // Refresh Files
  on(ViewerActions.RefreshFileAndAssociatedFiles, (state, { file }) => ({
    ...state,
    filesLoading: true,
  })),
  on(ViewerActions.RefreshFileAndAssociatedFilesSuccess, (state, { files }) => ({
    ...state,
    files: filesAdapter.upsertMany(files, state.files),
    filesLoading: initialState.filesLoading,
    filesLoaded: true,
  })),
  on(ViewerActions.RefreshFileAndAssociatedFilesFailure, (state, { error }) => ({
    ...state,
    filesError: error,
    filesLoading: initialState.filesLoading,
  })),
  // Files - Request Transfer and Files Download Urls
  on(ViewerActions.RequestDownloadUrls, (state) => ({
    ...state,
    urlsToDownload: initialState.urlsToDownload,
  })),
  on(ViewerActions.RequestDownloadUrlsSuccess, (state, { urls }) => ({
    ...state,
    urlsToDownload: urls,
  })),
  // Display Download Section
  on(ViewerActions.DisplayDownloadSection, (state) => ({
    ...state,
    downloadDisplayed: true,
  })),
  on(ViewerActions.HideDownloadSection, (state) => ({
    ...state,
    downloadDisplayed: false,
  })),
);

export const getViewerState = createFeatureSelector<State>('viewer');

export const getTransferMetas = createSelector(getViewerState, (state: State) => state.transferMetas);
export const getTransferMetasError = createSelector(getViewerState, (state: State) => state.transferMetasError);
export const getPreview = createSelector(getViewerState, (state: State) => state.preview);
export const getPreviewLoading = createSelector(getViewerState, (state: State) => state.previewLoading);
export const getPreviewLoaded = createSelector(getViewerState, (state: State) => state.previewLoaded);
export const getPreviewEnabled = createSelector(getViewerState, (state: State) => state.preview?.transfer.preview === Constant.previewStatus.Full);
export const getPreviewIsInQueue = createSelector(getViewerState, (state: State) => {
  if (state?.preview?.transfer?.availabilityStartDate) {
    return moment().diff(moment(state?.preview?.transfer?.availabilityStartDate)) <= 0;
  }
  return true;
});
export const getPreviewLoadingError = createSelector(getViewerState, (state: State) => state.previewLoadingError);
export const getPreviewUnlockError = createSelector(getViewerState, (state: State) => state.previewUnlockError);

export const getPromotion = createSelector(getViewerState, (state: State) => state.promotion);
export const getPromotionLoading = createSelector(getViewerState, (state: State) => state.promotionLoading);
export const getPromotionLoaded = createSelector(getViewerState, (state: State) => state?.promotionLoaded);

export const getCover = createSelector(getViewerState, (state: State) => state.cover);
export const getLogo = createSelector(getViewerState, (state: State) => state.logo);
export const getSnackbarDisplayed = createSelector(getViewerState, (state: State) => state.snackbarDisplayed);

export const getCoverBackgroundLoaded = createSelector(getViewerState, (state: State) => state.coverBackgroundLoaded);
export const getModalPreviewDisplayed = createSelector(getViewerState, (state: State) => state.modalPreviewDisplayed);
export const getMenuDisplayed = createSelector(getViewerState, (state: State) => state.menuDisplayed);
export const getPasswordRequired = createSelector(getViewerState, (state: State) => state.passwordRequired);
export const getAccessTrackingRequired = createSelector(getViewerState, (state: State) => state.accessTrackingRequired);
export const getAccessTracking = createSelector(getViewerState, (state: State) => state.previewAccessTracking);
export const getError = createSelector(getViewerState, (state: State) => state.errorMessage);
export const getPassword = createSelector(getViewerState, (state: State) => state.previewPassword);

export const getFilesState = createSelector(getViewerState, (state: State) => state.files);
export const getFiles = filesAdapter.getSelectors(getFilesState).selectAll;
export const getNextFile = createSelector(getViewerState, (state: State) => state.nextFile);
export const getUrlsToDownload = createSelector(getViewerState, (state: State) => state.urlsToDownload);
export const getCompleteTransferDownloadUrl = createSelector(getViewerState, (state: State) => state.completeTransferDownloadUrl);
export const getFilesUrlsExpiry = createSelector(getViewerState, (state: State) => state.filesUrlsExpiry);
export const getFilesLoading = createSelector(getViewerState, (state: State) => state.filesLoading);
export const getFilesLoaded = createSelector(getViewerState, (state: State) => state.filesLoaded);
export const getFilesError = createSelector(getViewerState, (state: State) => state.filesError);
export const getSelectedFiles = createSelector(getViewerState, (state: State) => state.selectedFiles);
export const getSelectedFilesTotalSize = createSelector(getViewerState, (state: State) => {
  let totalSize = 0;
  state.selectedFiles.forEach(item => {
    totalSize += item.size;
  });
  return totalSize;
});
export const getSelectedModalFile = createSelector(getViewerState, (state: State) => state.selectedModalFile);
export const getSelectedFileOffsetY = createSelector(getViewerState, (state: State) => state.selectedFileOffsetY);
export const getDownloadDisplayed = createSelector(getViewerState, (state: State) => state.downloadDisplayed);
